import { getQueryClient } from '@/api/queryClient';
import { graphql, type ResultOf, type VariablesOf } from '@/graphql';
import logToDatadog from '@/logger/logToDatadog';
import gqlClient from '@/utils/gqlClient';

import {
  getCartIdCookie,
  invalidateCartData,
  saveCartIdCookie,
} from '../utils/cartUtils';

export const mutation = graphql(`
  mutation AddToCart(
    $cartId: String
    $skuCode: String!
    $warrantySkuCode: String
  ) {
    cartAddLineItem(
      cartId: $cartId
      skuCode: $skuCode
      warrantySkuCode: $warrantySkuCode
    ) {
      cartId
      resultCategory
      errorCode
      invalidReason
    }
  }
`);

export type AddToCartResult = ResultOf<typeof mutation>;
export type AddToCartVariables = VariablesOf<typeof mutation>;

export function withQueryKey(vars: AddToCartVariables) {
  return {
    queryKey: ['addToCart', vars.cartId, vars.skuCode],
    queryFn: async () => {
      try {
        const response = await gqlClient().request(mutation, vars);
        const result = response.cartAddLineItem;

        if (result.resultCategory !== 'success')
          throw new Error(`AddToCart failed: ${result.invalidReason}`);

        if (result.cartId) {
          saveCartIdCookie(result.cartId);
        }

        invalidateCartData();

        return result;
      } catch (error) {
        logToDatadog({
          error,
          message: 'AddToCart query failed',
          module: 'cart',
        });
        throw error;
      }
    },
  };
}

export async function addToCart(
  skuCode: string,
  warrantySkuCode: string | null = null,
) {
  const cartId = getCartIdCookie();
  warrantySkuCode = warrantySkuCode ?? null;
  const queryClient = getQueryClient();

  return queryClient.fetchQuery({
    ...withQueryKey({ cartId, skuCode, warrantySkuCode }),
    staleTime: 0,
  });
}
